import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { MoreVert, Replay } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { dispatchTicketUpdated } from "../../utils/ticketEvents";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(1),
		},
	},
}));

const getAssignmentMode = mode =>
	mode === "normal" ? "normal" : "multiagent";

const TicketActionButtons = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	const assignmentModeStorageKey = `ticketAssignmentMode:${
		user?.companyId || user?.company?.id || "default"
	}`;
	const [assignmentMode, setAssignmentMode] = useState(() => {
		const storedMode = localStorage.getItem(assignmentModeStorageKey);
		return getAssignmentMode(storedMode);
	});
	const isNormalMode = assignmentMode === "normal";

	useEffect(() => {
		const storedMode = localStorage.getItem(assignmentModeStorageKey);
		if (storedMode) {
			setAssignmentMode(getAssignmentMode(storedMode));
		}

		const handleAssignmentModeChanged = event => {
			setAssignmentMode(getAssignmentMode(event?.detail?.mode));
		};

		window.addEventListener(
			"ticketAssignmentModeChanged",
			handleAssignmentModeChanged
		);

		return () => {
			window.removeEventListener(
				"ticketAssignmentModeChanged",
				handleAssignmentModeChanged
			);
		};
	}, [assignmentModeStorageKey]);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			const { data } = await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});
			dispatchTicketUpdated(data);

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	return (
		<div className={classes.actionButtons}>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					{!isNormalMode && (
						<ButtonWithSpinner
							loading={loading}
							startIcon={<Replay />}
							size="small"
							onClick={e => handleUpdateTicketStatus(e, "pending", null)}
						>
							{i18n.t("messagesList.header.buttons.return")}
						</ButtonWithSpinner>
					)}
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}
		</div>
	);
};

export default TicketActionButtons;

